
import { Home, ListTodo, BookOpen, User, Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useGeneration } from '../context/GenerationContext';
import { useMailContext } from '../context/MailContext';
import { useTasks } from '../hooks/useTasks';

export function MobileBottomMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const customNavigate = useCustomNavigate();
  const { markAllTasksAsViewed } = useGeneration();
  const mailContext = useMailContext();
  
  // Используем новый хук для получения задач
  const { data: tasksData } = useTasks();
  const tasks = tasksData?.tasks || [];

  // Проверяем, находимся ли мы на странице авторизации
  const isAuthPage = location.pathname.startsWith('/auth/');
  
  // Скрываем меню на страницах авторизации и лендинге
  if (isAuthPage || location.pathname === '/') {
    return null;
  }

  // Определяем активную вкладку
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.startsWith('/studio')) return 'home';
    if (path.startsWith('/tasks') || path.startsWith('/task/')) return 'tasks';
    if (path.startsWith('/knowledge')) return 'knowledge';
    if (path.startsWith('/mail')) return 'mail';
    if (path.startsWith('/profile')) return 'profile';
    return '';
  };

  const activeTab = getActiveTab();

  // Логика для счетчика задач
  // 1. Считаем непросмотренные завершенные задачи (исключая pending)
  const unviewedCompletedTasks = tasks.filter(task => !task.isViewed && task.status !== 'pending');
  const unviewedCompletedTaskCount = unviewedCompletedTasks.length;
  
  // 2. Считаем задачи в процессе (для анимации)
  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const pendingTaskCount = pendingTasks.length;
  const hasInProgressTasks = pendingTaskCount > 0;
  
  // 3. Общий счетчик для отображения (завершенные + в процессе)
  const totalTaskCount = unviewedCompletedTaskCount + pendingTaskCount;
  const displayTaskCount = Math.min(totalTaskCount, 9); // Отображаем макс. 9

  const unreadMailCount = mailContext?.unreadCount ?? 0;

  const menuItems = [
    {
      id: 'home',
      label: 'Главная',
      icon: Home,
      onClick: () => customNavigate('/studio'),
    },
    {
      id: 'tasks',
      label: 'История',
      icon: ListTodo,
      onClick: () => {
        // Сначала мгновенно переходим на страницу задач
        navigate('/tasks');
        // Затем выполняем пометку задач в фоне
        markAllTasksAsViewed().catch(error => {
          console.error('Ошибка при пометке всех задач как просмотренных:', error);
        });
      },
      showBadge: totalTaskCount > 0,
      badgeCount: displayTaskCount,
      hasAnimation: hasInProgressTasks,
    },
    {
      id: 'knowledge',
      label: 'База знаний',
      icon: null, // Используем кастомную иконку
      onClick: () => customNavigate('/knowledge/root'),
    },
    {
      id: 'mail',
      label: 'Почта',
      icon: Mail,
      onClick: () => navigate('/mail'),
      showBadge: unreadMailCount > 0,
      badgeCount: Math.min(unreadMailCount, 9),
    },
    {
      id: 'profile',
      label: 'Профиль',
      icon: User,
      onClick: () => navigate('/profile'),
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 md:hidden mobile-bottom-menu z-50">
      <div className="flex items-center h-full px-1 w-full">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`relative flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-200 ${
                item.id === 'knowledge' 
                  ? 'flex-[1.2] -translate-y-0.5' // База знаний всегда приподнята и шире
                  : 'flex-1' // Остальные равной ширины
              } ${
                item.id === 'knowledge'
                  ? isActive 
                    ? 'text-blue-600 dark:text-blue-400 mobile-menu-item-active' // База знаний активна - только цвет
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200' // База знаний неактивна
                  : isActive
                    ? 'text-blue-600 dark:text-blue-400 -translate-y-0.5 mobile-menu-item-active' // Обычная вкладка активна
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200' // Обычная вкладка неактивна
              }`}
            >
              <div className="relative">
                {item.id === 'knowledge' ? (
                  <img src="/icon.png" alt="База знаний" className="w-5 h-5" />
                ) : IconComponent ? (
                  <IconComponent className="w-5 h-5" />
                ) : null}
                
                {/* Значок для задач */}
                {item.id === 'tasks' && item.showBadge && (
                  <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5">
                    {/* Анимированная рамка для задач в процессе */}
                    {item.hasAnimation && (
                      <div className="absolute -inset-0.5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin z-20"></div>
                    )}
                    {/* Синий кружок с белой цифрой */}
                    <span className="absolute inset-0 bg-blue-600 text-white text-xs leading-none flex items-center justify-center rounded-full z-10">
                      {item.badgeCount}
                    </span>
                  </div>
                )}

                {/* Значок для почты */}
                {item.id === 'mail' && item.showBadge && (
                  <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5">
                    {/* Синий кружок с белой цифрой */}
                    <span className="absolute inset-0 bg-blue-600 text-white text-xs leading-none flex items-center justify-center rounded-full z-10">
                      {item.badgeCount}
                    </span>
                  </div>
                )}
              </div>
              <span className="text-xs font-medium truncate">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
} 